import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoader, formatDate, formatTime } from "@/components/common";
import InterestsCheckIn from "@/components/InterestsCheckIn";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Compass, CalendarHeart, ShieldAlert, ArrowRight, Users, Vote, MapPin, Infinity as InfinityIcon } from "lucide-react";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } } };

export default function Home() {
    const { user } = useAuth();
    const { data: full } = useQuery({ queryKey: ["me-full"], queryFn: async () => (await api.get("/me/full")).data });
    const { data: outings } = useQuery({ queryKey: ["my-outings"], queryFn: async () => (await api.get("/me/outings")).data });
    const { data: groups } = useQuery({ queryKey: ["my-groups"], queryFn: async () => (await api.get("/me/groups")).data });
    const { data: membership } = useQuery({ queryKey: ["me-membership"], queryFn: async () => (await api.get("/me/membership")).data });

    if (!full) return <PageLoader />;
    const eligible = full.eligible;
    const resetDate = (() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth() + 1, 1).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    })();
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = (outings || []).filter((o) => (o.date || "") >= today && o.status !== "completed" && o.my_attendance !== "canceled");
    const nextOuting = upcoming[0];
    const pendingGroups = (groups || []).filter((g) => g.status === "voting" || g.status === "awaiting_confirmation");

    return (
        <motion.div className="space-y-6" variants={container} initial="hidden" animate="show">
            <motion.div variants={item}>
                <p className="text-sm text-muted-foreground">Welcome back,</p>
                <h1 className="font-display text-3xl font-700 tracking-tight text-primary">{user?.first_name} 👋</h1>
            </motion.div>

            <motion.div variants={item}>
                <InterestsCheckIn preferences={full.preferences} confirmedAt={full.interests_confirmed_at} />
            </motion.div>

            {!eligible && (
                <motion.div variants={item} className="flex items-start gap-3 rounded-2xl border border-amber-300/60 bg-amber-50 p-4" data-testid="eligibility-banner">
                    <ShieldAlert className="mt-0.5 h-5 w-5 flex-none text-amber-600" />
                    <div className="text-sm text-amber-800">
                        <p className="font-600">You're almost ready to join activities.</p>
                        <p className="mt-1">
                            {full.membership?.status !== "active"
                                ? "Activate your $20/month membership to start matching."
                                : user?.verification_status !== "verified"
                                ? "Your account is pending verification. We'll notify you once you're a Verified Member."
                                : "Your account needs attention before you can join."}
                        </p>
                        {full.membership?.status !== "active" && (
                            <Link to="/profile?tab=membership"><Button size="sm" className="mt-3 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="banner-subscribe">Activate membership</Button></Link>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Outings-this-month meter */}
            {membership && full.membership?.status === "active" && (
                <motion.section variants={item} className="rounded-2xl bg-card p-5 shadow-sm" data-testid="cap-meter">
                    {membership.outing_cap ? (
                        <>
                            <div className="flex items-center justify-between">
                                <p className="font-600 text-primary">Outings this month</p>
                                <p className="text-sm font-700 text-primary" data-testid="cap-meter-count">{membership.outings_used} / {membership.outing_cap}</p>
                            </div>
                            <Progress value={Math.min(100, (membership.outings_used / membership.outing_cap) * 100)} className="mt-3 h-2" />
                            {membership.outings_used >= membership.outing_cap ? (
                                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                                    <p className="text-sm font-600 text-accent">You've used all {membership.outing_cap} Standard outings this month.</p>
                                    <Link to="/profile?tab=membership"><Button size="sm" data-testid="cap-meter-upgrade" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">Upgrade to Unlimited</Button></Link>
                                </div>
                            ) : (
                                <p className="mt-2 text-xs text-muted-foreground">{membership.outing_cap - membership.outings_used} outing{membership.outing_cap - membership.outings_used === 1 ? "" : "s"} left this month on Standard.</p>
                            )}
                            <p className="mt-1 text-xs text-muted-foreground" data-testid="cap-reset-hint">Your 5 outings refill on {resetDate}.</p>
                        </>
                    ) : (
                        <div className="flex items-center gap-3 text-primary">
                            <div className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-accent/10 text-accent"><InfinityIcon className="h-5 w-5" /></div>
                            <div>
                                <p className="font-600">Unlimited outings</p>
                                <p className="text-xs text-muted-foreground">{membership.outings_used} joined this month · no monthly limit</p>
                            </div>
                        </div>
                    )}
                </motion.section>
            )}

            {/* MAIN CTA */}
            <motion.div variants={item}>
            <Link to="/discover" data-testid="find-something-cta">
                <motion.div whileTap={{ scale: 0.98 }} className="group relative overflow-hidden rounded-3xl bg-primary p-8 text-primary-foreground shadow-md">
                    <div className="relative z-10">
                        <p className="text-sm text-white/70">What do you feel like doing?</p>
                        <h2 className="mt-2 font-display text-3xl font-700 leading-tight sm:text-4xl">Find Something to Do</h2>
                        <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 font-600 text-accent-foreground">
                            Let's go <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </span>
                    </div>
                    <Compass className="absolute -right-6 -top-6 h-40 w-40 text-white/10" />
                </motion.div>
            </Link>
            </motion.div>

            {/* Upcoming outing */}
            {nextOuting && (
                <motion.section variants={item}>
                    <h3 className="mb-3 font-display text-lg font-700 text-primary">Your next outing</h3>
                    <Link to={`/outings/${nextOuting.id}`} data-testid="home-next-outing">
                        <div className="flex items-center gap-4 rounded-2xl bg-card p-5 shadow-sm">
                            <div className="grid h-14 w-14 flex-none place-items-center rounded-2xl bg-secondary text-2xl">{nextOuting.activity_emoji}</div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate font-display text-lg font-700 text-primary">{nextOuting.activity_name}</p>
                                <p className="text-sm text-muted-foreground">{formatDate(nextOuting.date)} · {formatTime(nextOuting.start_time)}</p>
                                {nextOuting.venue_name && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {nextOuting.venue_name}</p>}
                            </div>
                            <ArrowRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                    </Link>
                </motion.section>
            )}

            {/* Potential groups */}
            {pendingGroups.length > 0 && (
                <motion.section variants={item}>
                    <h3 className="mb-3 font-display text-lg font-700 text-primary">Groups forming</h3>
                    <div className="space-y-3">
                        {pendingGroups.map((g) => (
                            <Link key={g.id} to={`/discover?group=${g.id}`} data-testid="home-group-item">
                                <div className="flex items-center gap-4 rounded-2xl bg-card p-5 shadow-sm">
                                    <div className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-accent/10 text-accent"><Vote className="h-5 w-5" /></div>
                                    <div className="flex-1">
                                        <p className="font-600 text-primary">Vote needed · {g.activities?.map((a) => a.name).slice(0, 3).join(", ")}</p>
                                        <p className="text-sm text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> {g.member_count} members interested</p>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-muted-foreground" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </motion.section>
            )}

            {/* Quick stats */}
            <motion.section variants={item} className="grid grid-cols-2 gap-3">
                <Link to="/profile?tab=availability" className="rounded-2xl bg-card p-5 shadow-sm" data-testid="home-availability-card">
                    <CalendarHeart className="h-5 w-5 text-accent" />
                    <p className="mt-2 font-display text-2xl font-700 text-primary">{full.availability.length} of 5</p>
                    <p className="text-xs text-muted-foreground">Upcoming Windows</p>
                </Link>
                <Link to="/profile?tab=preferences" className="rounded-2xl bg-card p-5 shadow-sm" data-testid="home-prefs-card">
                    <Compass className="h-5 w-5 text-accent" />
                    <p className="mt-2 font-display text-2xl font-700 text-primary">{full.preferences.length}</p>
                    <p className="text-xs text-muted-foreground">activity interests</p>
                </Link>
            </motion.section>
        </motion.div>
    );
}
